import useSWR, { useSWRConfig } from 'swr';
import { RequestQuery } from '@/data/types/RequestQuery';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsDpvale } from 'integration/generated/transactions';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { error as logError } from '@/data/utils/loggerUtil';
import {
	DpvaleGrandRoleBody,
	DpvaleLoginPhoneBody,
	DpvaleLoginPhoneSendOtpBody,
	DpvaleRecurrenteBody,
	DpvaleSendSmsPhoneVerificationBody,
	DpvaleSmsPhoneVerificationBody,
	DpvaleValidateTokenBody,
	DpvaleVerifyCredentialsBody,
	PostDpvalePromotionBody,
} from 'integration/generated/transactions/data-contracts';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotifications } from '@/data/Content/Notifications';
import { processError } from '@/data/utils/processError';
import { useDpvaleSelected } from '@/data/Content/DpvaleSelected';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import valueIsString from '../../../utils/typeAssertions/valueIsString';

const postDpvalePromotionsFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: RequestQuery = {},
		data: PostDpvalePromotionBody,
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).postDpvalePromotion(storeId, query, data, params);
		return res;
	};

const dpValesDisponiblesFetcher =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsDpvale(pub).dpValesDisponibles(storeId, query, params);
		return res;
	};

const DATA_KEY_DP_VALE_PROMOTIONS = 'dpValePromotions';

export const useDpvalePromotion = (args?: PostDpvalePromotionBody) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const rawSwr = useSWR(
		storeId && args ? [{ storeId, ...args }, DATA_KEY_DP_VALE_PROMOTIONS] : null,
		async ([{ storeId, ...data }]) => {
			try {
				const response = await postDpvalePromotionsFetcher(true)(storeId, {}, data, params);
				return {
					promotions: response?.data,
					promotionsError: undefined,
				};
			} catch (error) {
				const promotionsError = error as TransactionErrorResponse;
				return {
					promotions: undefined,
					promotionsError,
				};
			}
		},
		{ revalidateOnMount: true }
	);

	return {
		...rawSwr,
	};
};

const DATA_KEY_DP_VALES_DISPONIBLES = 'dpValesDisponibles';

export const useDpValesDisponibles = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { user } = useUser();

	const rawSwr = useSWR(
		storeId && user?.isLoggedIn ? [{ storeId }, DATA_KEY_DP_VALES_DISPONIBLES] : null,
		async ([{ storeId }]) => dpValesDisponiblesFetcher(true)(storeId, {}, params),
		{
			shouldRetryOnError: false,
			onError: (error) => {
				logError(undefined, 'useDpValesDisponibles: error silenciado: %o', error);
			},
		}
	);

	const dpValesDisponibles = useMemo(() => {
		if (!rawSwr.data) return [];
		return Object.entries(rawSwr.data.dpvales).map(([key, dpvale]) => ({
			id: key,
			...dpvale,
		}));
	}, [rawSwr.data]);

	return {
		dpValesDisponibles,
		...rawSwr,
	};
};

export const useRefreshDpValesDisponibles = () => {
	const { mutate } = useSWRConfig();

	const refreshDpValesDisponibles = useCallback(
		() =>
			mutate(
				(key) => Array.isArray(key) && key[1] === DATA_KEY_DP_VALES_DISPONIBLES,
				undefined,
				{ revalidate: true }
			),
		[mutate]
	);

	return { refreshDpValesDisponibles };
};

const dpValeRecurrenteFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleRecurrenteBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeRecurrente(storeId, data, query, params);
		return res;
	};

export const useDpValeRecurrente = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const createDpValeRecurrente = useCallback(
		async (data: DpvaleRecurrenteBody) => {
			const res = await dpValeRecurrenteFetcher(true)(storeId, data, {}, params);

			if (!res?.response?.id_coupon) {
				const errorDetail =
					getValueByPathFromUnknown(res, ['response', 'error_detail']) ||
					getValueByPathFromUnknown(res, ['error_detail']);

				if (valueIsString(errorDetail)) {
					throw new Error(errorDetail);
				}

				const errorMessage =
						getValueByPathFromUnknown(res, ['response', 'errorMessage']) ||
						getValueByPathFromUnknown(res, ['errorMessage']);

				if (valueIsString(errorMessage)) {
					throw new Error(errorMessage);
				}

				throw new Error(
						'No fue posible generar el vale recurrente. Intente de nuevo más tarde.'
					);
			}

			return res.response;
		},
		[storeId, params]
	);

	return {
		createDpValeRecurrente,
	};
};

export const DATA_KEY_DP_VALE_VALIDATE_TOKEN = 'dpValeValidateToken';

const dpValeValidateTokenFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleValidateTokenBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeValidateToken(storeId, data, query, params);
		return res?.response;
	};

export const useDpValeValidateToken = (token?: string) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const rawSwr = useSWR(
		storeId && token ? [{ storeId, token }, DATA_KEY_DP_VALE_VALIDATE_TOKEN] : null,
		async ([{ storeId, token }]) => dpValeValidateTokenFetcher(true)(storeId, { token }, {}, params)
	);

	return {
		...rawSwr,
	};
};

export const dpValeSendSmsPhoneVerificationFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleSendSmsPhoneVerificationBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeSendSmsPhoneVerification(
			storeId,
			data,
			query,
			params
		);
		return res?.response;
	};

export const dpValeSmsPhoneVerificationFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleSmsPhoneVerificationBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeSmsPhoneVerification(
			storeId,
			data,
			query,
			params
		);
		return res?.response;
	};

export const useDpValeSmsPhoneVerification = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const sendSmsPhoneVerification = useCallback(
		async (data: DpvaleSendSmsPhoneVerificationBody) => {
			const res = await dpValeSendSmsPhoneVerificationFetcher(true)(storeId, data, {}, params);
			return res;
		},
		[storeId, params]
	);

	const smsPhoneVerification = useCallback(
		async (data: DpvaleSmsPhoneVerificationBody) => {
			const res = await dpValeSmsPhoneVerificationFetcher(true)(storeId, data, {}, params);
			return res;
		},
		[storeId, params]
	);

	return {
		sendSmsPhoneVerification,
		smsPhoneVerification,
	};
};

export const dpValeRedeemerForRegisterFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		redeemerId: string | number,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeRedeemerForRegister(
			storeId,
			redeemerId,
			query,
			params
		);
		return res;
	};

const DATA_KEY_DP_VALE_REDEEMER_FOR_REGISTER = 'dpValeRedeemerForRegister';

export const useDpValeRedeemerForRegister = (redeemerId?: string | number) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const rawSwr = useSWR(
		storeId && redeemerId
			? [{ storeId, redeemerId }, DATA_KEY_DP_VALE_REDEEMER_FOR_REGISTER]
			: null,
		async ([{ storeId, redeemerId }]) =>
			dpValeRedeemerForRegisterFetcher(true)(storeId, redeemerId, {}, params)
	);

	return {
		...rawSwr,
	};
};

export const dpValeExistingUserFetcher =
	(pub: boolean) =>
	async (storeId: string, email: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsDpvale(pub).dpValeExistingUser(storeId, email, query, params);
		return res;
	};

export const useDpValeExistingUser = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const searchDpValeExistingUser = useCallback(
		async (email: string) => {
			const res = await dpValeExistingUserFetcher(true)(storeId, email, {}, params);
			return res;
		},
		[storeId, params]
	);

	return {
		searchDpValeExistingUser,
	};
};

export const dpValeGrandRoleFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleGrandRoleBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeGrandRole(storeId, data, query, params);
		return res;
	};

export const useDpValeGrandRole = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const grandRole = useCallback(
		async (data: DpvaleGrandRoleBody) => {
			const res = await dpValeGrandRoleFetcher(true)(storeId, data, {}, params);
			return res;
		},
		[storeId, params]
	);

	return {
		grandRole,
	};
};

export const dpValeVerifyCredentialsFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleVerifyCredentialsBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeVerifyCredentials(storeId, data, query, params);
		return res;
	};

export const useDpValeVerifyCredentials = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const verifyCredentials = useCallback(
		async (data: DpvaleVerifyCredentialsBody) => {
			const res = await dpValeVerifyCredentialsFetcher(true)(storeId, data, {}, params);
			return res;
		},
		[storeId, params]
	);

	return {
		verifyCredentials,
	};
};

export const dpValeLoginPhoneSendOtpFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleLoginPhoneSendOtpBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeLoginPhoneSendOtp(storeId, data, query, params);
		return res;
	};

export const dpValeLoginPhoneFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: DpvaleLoginPhoneBody,
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsDpvale(pub).dpValeLoginPhone(storeId, data, query, params);
		return res;
	};

export const useDpValeLoginPhone = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();

	const loginPhone = useCallback(
		async (data: DpvaleLoginPhoneBody) => {
			try {
				const res = await dpValeLoginPhoneFetcher(true)(storeId, data, {}, params);
				return res;
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
			}
		},
		[storeId, params, notifyError]
	);

	const loginPhoneSendOtp = useCallback(
		async (data: DpvaleLoginPhoneSendOtpBody) => {
			try {
				const res = await dpValeLoginPhoneSendOtpFetcher(true)(storeId, data, {}, params);
				return res;
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
			}
		},
		[storeId, params, notifyError]
	);

	return {
		loginPhone,
		loginPhoneSendOtp,
	};
};

export const useDpvaleFlow = () => {
	const [cleanDpvaleSelected, setCleanDpvaleSelected] = useState(true);
	const { dpvaleSelected, setDpvaleSelected } = useDpvaleSelected();
	const {
		dpValesDisponibles,
		isLoading: isLoadingDpValesDisponibles,
		...rest
	} = useDpValesDisponibles();

	const currentSelectedDpvale = useMemo(() => {
		if (isLoadingDpValesDisponibles) {
			return null;
		}

		return dpValesDisponibles.find((dp) => dp.id === dpvaleSelected.id);
	}, [isLoadingDpValesDisponibles, dpvaleSelected.id, dpValesDisponibles]);

	useEffect(() => {
		if (isLoadingDpValesDisponibles) {
			return;
		}

		if (!dpvaleSelected.id || !dpValesDisponibles.length) {
			setCleanDpvaleSelected(false);
			return;
		}

		const dpvale = dpValesDisponibles.find((dp) => dp.id === dpvaleSelected.id);

		if (!dpvale?.id) {
			setDpvaleSelected({});
		}

		setCleanDpvaleSelected(false);
	}, [isLoadingDpValesDisponibles, dpvaleSelected.id, dpValesDisponibles, setDpvaleSelected]);

	return {
		currentSelectedDpvale,
		dpValesDisponibles,
		setDpvaleSelected,
		isLoading: isLoadingDpValesDisponibles || cleanDpvaleSelected,
		...rest,
	};
};

const dpValeUserFetcher =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsDpvale(pub).dpValeUser(storeId, query, params);
		return res;
	};

const DATA_KEY_DP_VALE_USER = 'dpValeUser';

export const useDpValeUser = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const rawSwr = useSWR(
		storeId ? [{ storeId }, DATA_KEY_DP_VALE_USER] : null,
		async ([{ storeId }]) => dpValeUserFetcher(true)(storeId, {}, params)
	);

	return {
		...rawSwr,
	};
};
